import { CustomerChatMessage } from '@varo/graphql-schema-types';
import React, { createContext, PropsWithChildren, useState } from 'react';

export type ChatContextProps = {
  history: Array<CustomerChatMessage>;
  setHistory: (
    func: (history: Array<CustomerChatMessage>) => Array<CustomerChatMessage>,
  ) => void;
  isChatStarted: boolean;
  setIsChatStarted: (isChatStarted: boolean) => void;
  isChatEndedFromEvent: boolean;
  setIsChatEndedFromEvent: (isChatEndedFromEvent: boolean) => void;
  chatSessionId: string;
  setChatSessionId: (chatSessionId: string) => void;
  queuePosition: CustomerChatMessage | undefined;
  setQueuePosition: (queuePosition: CustomerChatMessage) => void;
  isAgentTyping: boolean;
  setIsAgentTyping: (isAgentTyping: boolean) => void;
  isChattingWithBot: boolean;
  setIsChattingWithBot: (chattingWithBot: boolean) => void;
  isSessionActive: boolean;
  setIsSessionActive: (isSessionActive: boolean) => void;
  chatOutageMessage: string | undefined;
  setChatOutageMessage: (chatOutageMessage: string) => void;
  startBotChat: (sessionId?: string) => void;
  endChatLocally: () => void;
  clearSessionIdAfterSurvey: () => void;
};

export const ChatContext = createContext<ChatContextProps>({
  history: [],
  setHistory: () => {},
  isChatStarted: false,
  setIsChatStarted: () => {},
  isChatEndedFromEvent: false,
  setIsChatEndedFromEvent: () => {},
  chatSessionId: '',
  setChatSessionId: () => {},
  queuePosition: undefined,
  setQueuePosition: () => {},
  isAgentTyping: false,
  setIsAgentTyping: () => {},
  isChattingWithBot: false,
  setIsChattingWithBot: () => {},
  isSessionActive: false,
  setIsSessionActive: () => {},
  chatOutageMessage: undefined,
  setChatOutageMessage: () => {},
  startBotChat: () => {},
  endChatLocally: () => {},
  clearSessionIdAfterSurvey: () => {},
});

const ChatContextProvider = ({ children }: PropsWithChildren) => {
  const [history, setHistory] = useState<Array<CustomerChatMessage>>([]);
  const [isChatStarted, setIsChatStarted] = useState<boolean>(false);
  const [isChatEndedFromEvent, setIsChatEndedFromEvent] =
    useState<boolean>(false);
  const [isChattingWithBot, setIsChattingWithBot] = useState<boolean>(false);
  const [isSessionActive, setIsSessionActive] = useState<boolean>(false);
  const [chatSessionId, setChatSessionId] = useState<string>('');
  const [chatOutageMessage, setChatOutageMessage] = useState<string>();
  const [queuePosition, setQueuePosition] = useState<CustomerChatMessage>();
  const [isAgentTyping, setIsAgentTyping] = useState<boolean>(false);

  const startBotChat = (sessionId?: string) => {
    if (sessionId) {
      setChatSessionId(sessionId);
    }
    setIsChatStarted(true);
    setIsChattingWithBot(true);
    setIsChatEndedFromEvent(false);
  };

  const endChatLocally = () => {
    setIsChatEndedFromEvent(true);
    setIsChatStarted(false);
    setIsChattingWithBot(false);
  };

  const clearSessionIdAfterSurvey = () => {
    setChatSessionId('');
  };

  return (
    <ChatContext.Provider
      value={{
        history,
        setHistory,
        isChatStarted,
        setIsChatStarted,
        isChatEndedFromEvent,
        setIsChatEndedFromEvent,
        chatSessionId,
        setChatSessionId,
        queuePosition,
        setQueuePosition,
        isAgentTyping,
        setIsAgentTyping,
        isChattingWithBot,
        setIsChattingWithBot,
        isSessionActive,
        setIsSessionActive,
        chatOutageMessage,
        setChatOutageMessage,
        startBotChat,
        endChatLocally,
        clearSessionIdAfterSurvey,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContextProvider;
